import { cn } from '@/lib/utils/cn';
import { VisuallyHidden } from './visually-hidden';

type Size = 'sm' | 'md' | 'lg';
type Variant = 'app' | 'button';

export interface SpinnerProps {
  /** sm=16px (h-4 w-4), md=24px (h-6 w-6), lg=32px (h-8 w-8) */
  size?: Size;
  /**
   * `app` — neutral on-surface: `border-zen-text` ring with transparent top.
   * `button` — inherits foreground via `currentColor` for use inside buttons
   *  so the spinner matches the enclosing button variant's text color.
   */
  variant?: Variant;
  /** Extra classes merged via `cn`. */
  className?: string;
  /**
   * Visually-hidden label exposed to assistive tech. When provided the
   * root element carries `role="status"` and renders a screen-reader-only
   * description; when omitted the spinner is purely decorative (`aria-hidden`).
   */
  label?: string;
}

/**
 * Reusable loading indicator. Uses CSS border-spin pattern (no SVG) so it
 * paints before JS hydrates and costs nothing at runtime.
 */
export function Spinner({ size = 'md', variant = 'app', className, label }: SpinnerProps) {
  const sizeClass = size === 'sm' ? 'h-4 w-4 border-2' : size === 'lg' ? 'h-8 w-8 border-4' : 'h-6 w-6 border-2';

  const variantClass =
    variant === 'button' ? 'border-current border-t-transparent' : 'border-zen-text border-t-transparent';

  const a11yProps = label
    ? { role: 'status' as const, 'aria-live': 'polite' as const }
    : ({ 'aria-hidden': true } as const);

  return (
    <span
      className={cn('inline-block animate-spin rounded-full align-[-0.125em]', sizeClass, variantClass, className)}
      {...a11yProps}
    >
      {label ? <VisuallyHidden>{label}</VisuallyHidden> : null}
    </span>
  );
}
